import MainLayout from "../../../../layouts/MainLayout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import Form from "../../../../components/forms/Form";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import "../../../../assets/styles/NestedCheckboxSelector/index.css";
import { faBuilding, faDesktop } from "@fortawesome/free-solid-svg-icons";
import GenericBox from "../../../../components/GenericBox";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePermissions } from "../../../../hooks/usePermissions";
import useForm from "../../../../hooks/useForm";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const UserOrganizationsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      username: [],
      email: [],
      deleted_at: false,
    },
    "users",
  );

  const { showLoader, hideLoader } = useLoader();
  const { get: baseServiceGet, post: baseServicePost } =
    useBaseService(navigate);

  const { formData, setFormData } = useForm({
    applications_organizations: [],
  });

  // Bucar por Aplicaçãoes
  const { data: applicationsResponseData, isLoading: isLoadingApplications } =
    useQueryWithLoader({
      queryKey: ["applications"],
      queryFn: async () => {
        showLoader();
        const response = await baseServiceGet(entities.applications.get);
        return response.result.data || [];
      },
      staleTime: 5 * 60 * 1000,
      select: (data) => {
        return {
          applications: data.map((application) => ({
            id: application.id,
            name: application.name,
            deleted_at: !application.deleted_at ? "Ativo" : "Inativo",
            isDeleted: !!application.deleted_at,
          })),
          totalPages: data.last_page,
        };
      },
    });

  const { data: organizationsResponseData } = useQueryWithLoader({
    queryKey: ["organizations"],
    queryFn: async () => {
      const response = await baseServiceGet(entities.organizations.get);
      return response.result.data || [];
    },
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      return {
        organizations: data.map((organization) => ({
          id: organization.id,
          name: organization.name,
          deleted_at: !organization.deleted_at ? "Ativo" : "Inativo",
          isDeleted: !!organization.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
  });

  // Buscar por aplicações organizações do usuário
  const { data: userApplicationsData } = useQueryWithLoader({
    queryKey: ["userApplications", id],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.users.applications.organizations(id),
      );
      console.log("userApplications", response);
      return response.result || [];
    },
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      const userAppMap = {};

      data.data.forEach(({ application_id, organization_id }) => {
        if (!userAppMap[application_id]) {
          userAppMap[application_id] = {};
        }
        userAppMap[application_id][organization_id] = true;
      });

      return userAppMap;
    },
  });

  // Gerar dados iniciais para o formulário
  useEffect(() => {
    if (
      applicationsResponseData?.applications &&
      organizationsResponseData?.organizations &&
      userApplicationsData
    ) {
      const initialData = generateInitialFormData(
        applicationsResponseData.applications,
        organizationsResponseData.organizations,
        userApplicationsData, // já vem como { [application_id]: { [organization_id]: true } }
      );
      setFormData(initialData);
    }
  }, [
    applicationsResponseData,
    organizationsResponseData,
    userApplicationsData,
  ]);

  const generateInitialFormData = (
    applications,
    organizations,
    userAppsMap,
  ) => {
    return applications.reduce((acc, application) => {
      acc[application.id] = organizations.reduce((accOrg, org) => {
        accOrg[org.id] = userAppsMap?.[application.id]?.[org.id] || false;
        return accOrg;
      }, {});
      return acc;
    }, {});
  };

  const handleChange = (event) => {
    const { id, checked } = event.target;

    if (id.includes("primary")) {
      handlePrimaryCheckboxChange(id);
    } else if (id.includes("secundary")) {
      handleSecondaryCheckboxChange(id, checked);
    }
  };

  const handlePrimaryCheckboxChange = (id) => {
    const applicationId = id.split("-")[1];
    const isAllCheckBoxSelected = isAllSelected(applicationId);

    setFormData((prev) => ({
      ...prev,
      [applicationId]: {
        ...prev[applicationId], // Mantenha o restante dos dados de `formData[applicationId]` intacto
        ...Object.keys(prev[applicationId] || {}).reduce(
          (acc, currentValue) => {
            acc[currentValue] = !isAllCheckBoxSelected;
            return acc;
          },
          {},
        ),
      },
    }));
  };

  const handleSecondaryCheckboxChange = (id, checked) => {
    const [, applicationId, organizationId] = id.split("-");
    setFormData((prev) => ({
      ...prev,
      [applicationId]: {
        ...prev[applicationId],
        [organizationId]: checked,
      },
    }));
  };

  const isAllSelected = (applicationId) => {
    const formDataForApplication = formData[applicationId];
    // Verifica se formDataForApplication é um objeto válido e não nulo
    if (
      !formDataForApplication ||
      Object.keys(formDataForApplication).length === 0
    ) {
      return false; // Se não tiver dados para essa aplicação, não está tudo selecionado
    }
    return Object.values(formDataForApplication).every(
      (value) => value === true,
    );
  };

  const syncMultipleUserAppOrganizationsMutation = useMutation({
    mutationFn: async (data) => {
      const response = await baseServicePost(
        entities.users.applications.create(id),
        data,
      );
      return response;
    },
    onSuccess: (data) => {
      if (data.status === 200) {
        hideLoader();
        queryClient.invalidateQueries({ queryKey: ["userApplications", id] });
        queryClient.invalidateQueries({
          queryKey: ["users", filters, pagination],
        });
        navigate(`/aluguel/usuarios`);
      }
      hideLoader();
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const handleSubmit = async () => {
    try {
      const data = Object.keys(formData).reduce((acc, currentValue) => {
        const organizations = Object.entries(formData[currentValue])
          .map(([key, value]) => (value ? key : null)) // Pegando as chaves dos ids das organizações
          .filter(Boolean); // Filtra valores nulos ou falsos

        if (organizations.length > 0) {
          // Agora, o formato de cada item será { application_id, organizations }
          acc.push({ application_id: currentValue, organizations });
        }
        return acc;
      }, []);

      // Agora, passamos o array `applications_organizations` como esperado pela API
      await syncMultipleUserAppOrganizationsMutation.mutateAsync({
        applications_organizations: data,
      });
    } catch (error) {
      console.error("Erro ao associar usuários com as organizações: ", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = () => {
    navigate("/usuarios/");
  };

  return (
    <MainLayout>
      <PageHeader
        title="Organizações do Usuário"
        showBackButton={true}
        backUrl="/aluguel/usuarios/"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Associar"
          textLoadingSubmit="Associando"
          handleBack={handleBack}>
          {() => (
            <div className="container-boxs">
              {applicationsResponseData?.applications?.map((application) => (
                <GenericBox
                  key={application.id}
                  item={application}
                  subItems={organizationsResponseData?.organizations}
                  handleChange={handleChange}
                  formData={formData}
                  isAllSelected={isAllSelected}
                  iconPrimary={faDesktop}
                  iconSecundary={faBuilding}
                />
              ))}
            </div>
          )}
        </Form>
      </div>
    </MainLayout>
  );
};

export default UserOrganizationsPage;
